import { useState } from 'react';
import { useUserContext } from '../../contexts/UserContext';
import { hasPermission, Permission } from '../../utils/permissions';
import { ServiceItemsTab } from './ServiceItemsTab';
import { ServiceProceduresTab } from './ServiceProceduresTab';
import { VendorsTab } from './VendorsTab';
import { ServiceCentersTab } from './ServiceCentersTab';
import { LaborRatesTab } from './LaborRatesTab';
import { CompanyResourcesTab } from './CompanyResourcesTab';
import { UnitsOfMeasureTab } from './UnitsOfMeasureTab';
import '../../css/CompanyResourcesPage.css';
import { PageHeader, TabNav, IconItems, IconClipboard, IconVendors, IconServiceCenter, IconDollar, IconResources, IconListSquare } from '../ui';
import type { TabNavItem } from '../ui';

type TabId = 'service-items' | 'service-procedures' | 'vendors' | 'service-centers' | 'labor-rates' | 'company-resources' | 'units-of-measure';

const TABS: (TabNavItem & { id: TabId })[] = [
    {
        id: 'service-items',
        ariaLabel: 'Service Items',
        label: (
            <>
                <span className="cr-tab-icon" aria-hidden="true">
                    <IconItems />
                </span>
                Service Items
            </>
        ),
    },
    {
        id: 'service-procedures',
        ariaLabel: 'Procedures',
        label: (
            <>
                <span className="cr-tab-icon" aria-hidden="true">
                    <IconClipboard />
                </span>
                Procedures
            </>
        ),
    },
    {
        id: 'vendors',
        ariaLabel: 'Vendors',
        label: (
            <>
                <span className="cr-tab-icon" aria-hidden="true">
                    <IconVendors />
                </span>
                Vendors
            </>
        ),
    },
    {
        id: 'service-centers',
        ariaLabel: 'Service Centers',
        label: (
            <>
                <span className="cr-tab-icon" aria-hidden="true">
                    <IconServiceCenter />
                </span>
                Service Centers
            </>
        ),
    },
    {
        id: 'labor-rates',
        ariaLabel: 'Labor Rates',
        label: (
            <>
                <span className="cr-tab-icon" aria-hidden="true">
                    <IconDollar />
                </span>
                Labor Rates
            </>
        ),
    },
    {
        id: 'company-resources',
        ariaLabel: 'Company Resources',
        label: (
            <>
                <span className="cr-tab-icon" aria-hidden="true">
                    <IconResources />
                </span>
                Company Resources
            </>
        ),
    },
    {
        id: 'units-of-measure',
        ariaLabel: 'Units of Measure',
        label: (
            <>
                <span className="cr-tab-icon" aria-hidden="true">
                    <IconListSquare />
                </span>
                Units of Measure
            </>
        ),
    },
];

export const CompanyResourcesPage = () => {
    const { companyOwner, companyUser, businessAdmin } = useUserContext();
    const [activeTab, setActiveTab] = useState<TabId>('service-items');

    const isAdmin = hasPermission(companyOwner, companyUser, Permission.MANAGE_COMPANY_RESOURCES, businessAdmin);
    const companyId = companyOwner?.companyId || companyUser?.companyId || '';

    return (
        <div className="page-container cr-container">
            <PageHeader
                title="Resources"
                signalLine="Your company's master data used across PM, Daily Service, Cost &amp; Tracking and reporting"
            />

            <TabNav
                variant="tabs"
                ariaLabel="Resource sections"
                className="tab-nav-stand-alone cr-tabs"
                tabs={TABS}
                activeTabId={activeTab}
                onTabChange={(tabId) => setActiveTab(tabId as TabId)}
            />

            {activeTab === 'service-items' && <ServiceItemsTab isAdmin={isAdmin} companyId={companyId} />}
            {activeTab === 'service-procedures' && <ServiceProceduresTab isAdmin={isAdmin} companyId={companyId} />}
            {activeTab === 'vendors' && <VendorsTab isAdmin={isAdmin} companyId={companyId} />}
            {activeTab === 'service-centers' && <ServiceCentersTab isAdmin={isAdmin} companyId={companyId} />}
            {activeTab === 'labor-rates' && <LaborRatesTab isAdmin={isAdmin} companyId={companyId} />}
            {activeTab === 'company-resources' && <CompanyResourcesTab isAdmin={isAdmin} companyId={companyId} />}
            {activeTab === 'units-of-measure' && <UnitsOfMeasureTab isAdmin={isAdmin} companyId={companyId} />}
        </div>
    );
};
